import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { Changed, Commit as CommitOut, Kind, PageCommit, SpaceOut } from "../../api/types.gen.ts";
import { SLOT, t } from "../../i18n/index.ts";
import { commentCount } from "../../lib/commentCounts.ts";
import { formatInstant, formatRelative } from "../../lib/format.ts";
import {
  ABOUT_ITEM_TEXT,
  andMore,
  bulkText,
  describe,
  didOf,
  isAboutItem,
  isReview,
  LINKS,
  lead,
  sharingText,
} from "../../lib/history.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { useResource } from "../../lib/resource.ts";
import { Person } from "../../ui/Avatar.tsx";
import { BookmarkButton } from "../../ui/BookmarkButton.tsx";
import { Link } from "../../ui/Button.tsx";
import { CommentCount } from "../../ui/counts.tsx";
import {
  CreatedIcon,
  DeletedIcon,
  EditedIcon,
  GuestIcon,
  OrgIcon,
  PrivateIcon,
  PublicIcon,
  UndoIcon,
} from "../../ui/icons.tsx";
import { Async, Badge, EmptyState, Slotted } from "../../ui/misc.tsx";
import { Pager } from "../../ui/Pager.tsx";
import { LINK } from "../../ui/styles.ts";
import { Table, Td, Th } from "../../ui/Table.tsx";
import { Via } from "../settings/activity.tsx";
import { WithEditors } from "./editors.tsx";

const PAGE_SIZE = 50;
/** A link to the object's page; plain text for deleted objects, which have none. */
function ObjectLink(p: { space: SpaceOut; kind: Kind; id: string; deleted?: boolean; children: ComponentChildren }) {
  const page = LINKS[p.kind];
  return p.deleted || page === null ? (
    p.children
  ) : (
    <Link href={spaceUrl(p.space, `${page}${p.id}`)} class={LINK}>
      {p.children}
    </Link>
  );
}

/** A review request in a history line: what happened to it, naming its item unless this is the item's history. */
function AboutItemLine({
  space,
  c,
  object,
}: {
  space: SpaceOut;
  c: Changed & { item: string; kind: "request" | "comment" };
  object: string | undefined;
}) {
  const [text, bare] = ABOUT_ITEM_TEXT[c.kind][c.action];
  return (
    <>
      {c.item === object ? (
        t(bare)
      ) : (
        <Slotted text={t(text, { label: SLOT })}>
          <ObjectLink space={space} kind="item" id={c.item}>
            {c.label || t("common.untitled")}
          </ObjectLink>
        </Slotted>
      )}
      {c.message ? <span class="text-zinc-500"> — {c.message}</span> : null}
    </>
  );
}

/**
 * A review in a history line: its findings (linking to it) and message, naming the reviewed item unless the list
 * is that item's own history (`object`).
 */
function ReviewLine({
  space,
  c,
  object,
}: {
  space: SpaceOut;
  c: Changed & { item: string };
  object: string | undefined;
}) {
  const deleted = c.action === "deleted";
  const item = (
    <ObjectLink space={space} kind="item" id={c.item}>
      {c.label || t("common.untitled")}
    </ObjectLink>
  );
  const own = c.item === object;
  if (deleted) {
    return own ? (
      t("history.reviewRemovedBare")
    ) : (
      <Slotted text={t("history.reviewRemoved", { label: SLOT })}>{item}</Slotted>
    );
  }
  return (
    <>
      <Badge tone="blue">{t("kind.review")}</Badge>{" "}
      {own ? null : (
        <>
          <Slotted text={t("history.reviewed", { label: SLOT })}>{item}</Slotted>{" "}
        </>
      )}
      <ObjectLink space={space} kind="review" id={c.id}>
        {t("item.findings", { count: c.findings ?? 0 })}
      </ObjectLink>
      {c.message ? <span class="text-zinc-500"> — {c.message}</span> : null}
    </>
  );
}

/** What a change did, with the object linked: "Updated item “PO-1043”". */
function ChangeLine({
  space,
  c,
  object,
  undo = false,
}: {
  space: SpaceOut;
  c: Changed;
  object: string | undefined;
  /** In an undo: making something is bringing it back. */
  undo?: boolean;
}) {
  if (isAboutItem(c)) {
    return <AboutItemLine space={space} c={c} object={object} />;
  }
  if (isReview(c)) {
    return <ReviewLine space={space} c={c} object={object} />;
  }
  const kind = t(`history.noun.${c.kind}`);
  const deleted = c.action === "deleted";
  const shared = sharingText(c, SLOT);
  if (shared) {
    return (
      <Slotted text={shared}>
        <ObjectLink space={space} kind={c.kind} id={c.id} deleted={false}>
          {c.label}
        </ObjectLink>
      </Slotted>
    );
  }
  if (!c.label) {
    return (
      <ObjectLink space={space} kind={c.kind} id={c.id} deleted={deleted}>
        {t(`history.didBare.${didOf(c, undo)}`, { kind })}
      </ObjectLink>
    );
  }
  return (
    <Slotted text={t(`history.did.${didOf(c, undo)}`, { kind, label: SLOT })}>
      <ObjectLink space={space} kind={c.kind} id={c.id} deleted={deleted}>
        {c.label}
      </ObjectLink>
    </Slotted>
  );
}

/**
 * A commit's line: its message (e.g. "Email from …"), or that it undid an earlier one (linked), and what it changed:
 * its most telling change linked, then what the rest did.
 */
function Summary(p: { space: SpaceOut; c: CommitOut; object: string | undefined }) {
  const { c } = p;
  // One thing done to many items: said in a line, whatever its changes number.
  if (c.bulk) {
    return <>{bulkText(c.bulk)}</>;
  }
  const first = lead(c.preview);
  const line = first ? <ChangeLine space={p.space} c={first} object={p.object} undo={Boolean(c.reverts)} /> : null;
  const changes =
    line && first && c.changes > 1 ? (
      <Slotted text={andMore(SLOT, first, c.changes, c.actions)}>{line}</Slotted>
    ) : (
      (line ?? t("history.changes", { count: c.changes }))
    );
  const before = c.message ? (
    c.message
  ) : c.reverts ? (
    <Link href={spaceUrl(p.space, `/history/${c.reverts}`)} class={LINK}>
      {t("history.undid")}
    </Link>
  ) : null;
  return before ? (
    <>
      {before}
      {line ? <span class="text-zinc-500"> — </span> : null}
      {line ? changes : null}
    </>
  ) : (
    changes
  );
}

/** What kind of change a commit is, at a glance: an undo, or what its most telling change did. */
function CommitIcon({ c }: { c: CommitOut }) {
  const first = lead(c.preview);
  if (c.reverts) {
    return <UndoIcon class="text-zinc-500" />;
  }
  const bulk = c.bulk?.action;
  if (bulk === "delete" || bulk === "purge" || bulk === "cleanup") {
    return <DeletedIcon class="text-red-600" />;
  }
  if (first?.action === "deleted") {
    return <DeletedIcon class="text-red-600" />;
  }
  if (first?.action === "created") {
    return <CreatedIcon class="text-green-600" />;
  }
  // Who sees an item: by what it is now.
  const sharing = first?.kind === "item" ? first.sharing : null;
  if (sharing) {
    return sharing.from === sharing.to ? (
      <GuestIcon class="text-amber-600" />
    ) : sharing.to === "public" ? (
      <PublicIcon class="text-amber-600" />
    ) : sharing.to === "private" ? (
      <PrivateIcon class="text-zinc-500" />
    ) : (
      <OrgIcon class="text-zinc-500" />
    );
  }
  return <EditedIcon class="text-zinc-500" />;
}

/** Commits as a table: when (to the commit's page), who (and how), and what it did. */
function CommitRows({ space, page, object }: { space: SpaceOut; page: PageCommit; object: string | undefined }) {
  return (
    <Table stack={true}>
      <thead class="bg-zinc-50">
        <tr>
          <Th>{t("history.when")}</Th>
          <Th>{t("history.author")}</Th>
          <Th>{t("history.summary")}</Th>
        </tr>
      </thead>
      <tbody class="divide-y divide-zinc-100">
        {page.data.map((c) => (
          <tr key={c.id} class={`group${isReview(c.preview[0]) ? " bg-blue-50/40" : ""}`}>
            <Td title={formatInstant(c.created_at)} label={t("history.when")}>
              <span class="inline-flex items-center gap-1">
                <Link href={spaceUrl(space, `/history/${c.id}`)} class={LINK}>
                  {formatRelative(c.created_at)}
                </Link>
                <BookmarkButton space={space} target={{ kind: "commit", commit: c.id }} size="2xs" quiet={true} />
              </span>
            </Td>
            <Td label={t("history.author")}>
              {c.author_name ? <Person name={c.author_name} id={c.author} /> : t("history.system")}
              {/* A live document's slice: everyone else who edited in it. */}
              <WithEditors c={c} class="block text-xs text-zinc-500" />
              {c.on_behalf_name ? (
                <span class="block text-xs text-zinc-500">{t("history.onBehalf", { name: c.on_behalf_name })}</span>
              ) : null}
              <span class="block">
                <Via c={c.client} />
              </span>
            </Td>
            <Td wide={true}>
              <span class="flex items-start gap-2">
                <span class="mt-0.5 shrink-0">
                  <CommitIcon c={c} />
                </span>
                <span class="min-w-0">
                  {/* A document's working version: its editor's autosave, replaced by the next until it's a version. */}
                  {c.working ? (
                    <span class="mr-2" title={t("history.workingHint")}>
                      <Badge tone="gray">{t("history.working")}</Badge>
                    </span>
                  ) : null}
                  <span class="mr-2" title={c.preview.map((x) => describe(x, Boolean(c.reverts))).join("\n")}>
                    <Summary space={space} c={c} object={object} />
                  </span>
                  <CommentCount
                    count={commentCount(space.id, "commits", c.id)}
                    href={spaceUrl(space, `/history/${c.id}?tab=conversation`)}
                  />
                </span>
              </span>
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Commits, newest first; only those that touched `object` when given (with `reviews`, also those reviewing it). */
export function CommitList({ space, object, reviews }: { space: SpaceOut; object?: string; reviews?: boolean }) {
  const [cursor, setCursor] = useState<string | null>(null);
  const list = useResource(`commits:${space.id}:${object}:${reviews}:${cursor}`, (signal) =>
    api.get<PageCommit>(
      spaceApi(space, "/commits"),
      { object, reviews: reviews === true || null, cursor, limit: PAGE_SIZE },
      signal,
    ),
  );
  return (
    <Async r={list}>
      {(page) =>
        page.data.length === 0 ? (
          <EmptyState title={t("history.empty")} />
        ) : (
          <>
            <Pager page={page} size={PAGE_SIZE} go={setCursor} class="mb-3" />
            <CommitRows space={space} page={page} object={object} />
            <Pager page={page} size={PAGE_SIZE} go={setCursor} class="mt-4" />
          </>
        )
      }
    </Async>
  );
}
